/**
 * ConfirmDestructiveDialog — modal reutilizable para acciones destructivas.
 *
 * Requiere que el usuario escriba un texto exacto antes de habilitar el botón
 * de confirmación (patrón anti-clic-accidente, estilo GitHub).
 *
 * Props:
 *   open              {bool}     — controla visibilidad
 *   onClose           {fn}       — callback al cerrar (sin confirmar)
 *   title             {string}   — título del modal
 *   description       {string|ReactNode} — descripción/warning
 *   expectedConfirmText {string} — texto que el usuario debe escribir para confirmar
 *   confirmLabel      {string}   — texto del botón de confirmación (default: "Delete")
 *   onConfirm         {fn}       — callback al confirmar (después de validar el texto)
 *   danger            {bool}     — si true, el botón es rojo (default: true)
 *   loading           {bool}     — deshabilita el botón mientras se procesa
 */
import { useState, useEffect } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

const ConfirmDestructiveDialog = ({
  open,
  onClose,
  title,
  description,
  expectedConfirmText,
  confirmLabel = "Delete",
  onConfirm,
  danger = true,
  loading = false,
}) => {
  const [inputValue, setInputValue] = useState("");

  // Limpiar input al abrir/cerrar el dialog
  useEffect(() => {
    if (open) {
      setInputValue("");
    }
  }, [open]);

  const isMatch = expectedConfirmText
    ? inputValue.trim() === expectedConfirmText
    : true;

  const handleConfirm = () => {
    if (!isMatch) return;
    onConfirm();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="confirm-destructive-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {danger && <AlertTriangle className="w-5 h-5 text-destructive" />}
            {title}
          </DialogTitle>
          {description && (
            <DialogDescription asChild>
              <div className="text-sm text-muted-foreground">{description}</div>
            </DialogDescription>
          )}
        </DialogHeader>

        {expectedConfirmText && (
          <div className="space-y-2 py-2">
            <Label className="text-xs text-muted-foreground">
              {`Type `}
              <span className="font-mono font-semibold text-foreground">
                {expectedConfirmText}
              </span>
              {` to confirm:`}
            </Label>
            <Input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={expectedConfirmText}
              autoComplete="off"
              data-testid="confirm-text-input"
              onKeyDown={(e) => {
                if (e.key === "Enter" && isMatch && !loading) handleConfirm();
              }}
            />
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant={danger ? "destructive" : "default"}
            onClick={handleConfirm}
            disabled={!isMatch || loading}
            data-testid="confirm-destructive-btn"
          >
            {loading ? "Processing..." : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmDestructiveDialog;
